import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader } from "@/components/Bits";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Upload, Loader2, Sparkles, X, Check, Search, AlertTriangle,
  ChevronRight, ChevronLeft,
} from "lucide-react";

const PROJECT_TYPES = [
  { v: "renovation", l: "Renovation", d: "Update or improve existing spaces" },
  { v: "maintenance", l: "Maintenance", d: "Repairs and routine upkeep" },
  { v: "extension", l: "Extension", d: "Add space to an existing home" },
  { v: "new_build", l: "New Build", d: "Build from the ground up" },
  { v: "commercial", l: "Commercial", d: "Office, retail or restaurant fit-out" },
];
const BUDGETS = [
  { v: "under_50k", l: "Under R50,000" },
  { v: "50k_150k", l: "R50,000 – R150,000" },
  { v: "150k_500k", l: "R150,000 – R500,000" },
  { v: "500k_1m", l: "R500,000 – R1m" },
  { v: "1m_plus", l: "R1m+" },
];
const TIMELINES = [
  { v: "asap", l: "ASAP" },
  { v: "1_month", l: "1 Month" },
  { v: "3_months", l: "3 Months" },
  { v: "6_months", l: "6 Months" },
];

const STEP_LABELS = ["Type", "Areas", "Services", "Scope", "Upload", "Budget"];

export default function SubmitProject() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [categories, setCategories] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Cache fetched services per category-id so revisits don't refetch.
  const [serviceCache, setServiceCache] = useState({}); // {catId: Service[]}

  const [form, setForm] = useState({
    title: "",
    project_type: "renovation",
    selectedCategoryIds: [],
    selectedServiceIds: [],
    scope_details: { area_size_m2: "", num_rooms: "", notes: "" },
    address: "",
    gps_lat: null, gps_lng: null,
    budget_range: "150k_500k",
    timeline: "3_months",
    description: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  // Load categories whenever project_type changes
  useEffect(() => {
    setLoadingCatalog(true);
    api.get(`/catalog/categories?project_type=${form.project_type}`)
      .then((r) => setCategories(r.data))
      .catch(() => setCategories([]))
      .finally(() => setLoadingCatalog(false));
  }, [form.project_type]);

  // Load services for the selected categories (cached per category).
  useEffect(() => {
    if (form.selectedCategoryIds.length === 0) { setAllServices([]); return; }
    const missing = form.selectedCategoryIds.filter((cid) => !serviceCache[cid]);
    if (missing.length === 0) {
      // All cached — merge straight away.
      const merged = form.selectedCategoryIds.flatMap((cid) => serviceCache[cid] || []);
      setAllServices(merged);
      return;
    }
    Promise.all(
      missing.map((cid) =>
        api.get(`/catalog/services?category_id=${cid}`).then((r) => [cid, r.data]).catch(() => [cid, []])
      )
    ).then((entries) => {
      const next = { ...serviceCache };
      entries.forEach(([cid, list]) => { next[cid] = list; });
      setServiceCache(next);
      const merged = form.selectedCategoryIds.flatMap((cid) => next[cid] || []);
      setAllServices(merged);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.selectedCategoryIds]);

  const toggleCategory = (cid) => {
    setForm((f) => {
      const has = f.selectedCategoryIds.includes(cid);
      const next = has ? f.selectedCategoryIds.filter((x) => x !== cid) : [...f.selectedCategoryIds, cid];
      // Drop services whose category was deselected
      const stillValid = new Set(allServices.filter((s) => next.includes(s.category_id)).map((s) => s.id));
      return {
        ...f,
        selectedCategoryIds: next,
        selectedServiceIds: f.selectedServiceIds.filter((sid) => stillValid.has(sid)),
      };
    });
  };

  const toggleService = (sid) => {
    setForm((f) => {
      const has = f.selectedServiceIds.includes(sid);
      return { ...f, selectedServiceIds: has ? f.selectedServiceIds.filter((x) => x !== sid) : [...f.selectedServiceIds, sid] };
    });
  };

  // Flags driven by selected service metadata
  const flags = useMemo(() => {
    const sel = allServices.filter((s) => form.selectedServiceIds.includes(s.id));
    return {
      surveyRequired: sel.some((s) => s.requires_site_survey),
      engineerRequired: sel.some((s) => s.requires_engineer),
      municipalApproval: sel.some((s) => s.requires_municipal_approval),
      avgDuration: sel.length
        ? Math.round(sel.reduce((a, s) => a + (s.default_duration_days || 0), 0) / sel.length)
        : 0,
    };
  }, [allServices, form.selectedServiceIds]);

  const next = () => setStep((s) => Math.min(s + 1, STEP_LABELS.length));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  // Per-step validation
  const canAdvance = () => {
    if (step === 1) return !!form.title && !!form.project_type;
    if (step === 2) return form.selectedCategoryIds.length > 0;
    if (step === 3) return form.selectedServiceIds.length > 0;
    if (step === 4) return !!form.address;
    return true;
  };

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true); setError("");
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, data]);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };
  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async () => {
    setSubmitting(true); setError("");
    try {
      const payload = {
        title: form.title,
        project_type: form.project_type,
        address: form.address,
        gps_lat: form.gps_lat,
        gps_lng: form.gps_lng,
        budget_range: form.budget_range,
        timeline: form.timeline,
        description: form.description,
        file_ids: files.map((f) => f.id),
        selected_service_ids: form.selectedServiceIds,
        scope_details: {
          ...form.scope_details,
          flags,
        },
      };
      const { data } = await api.post("/projects", payload);
      navigate(`/projects/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <PageHeader
          overline="New submission"
          title="Tell us about your project"
          description="Six quick steps. We'll generate an AI rough estimate the moment you submit."
        />

        {/* Stepper */}
        <div className="grid grid-cols-6 gap-2 mb-2">
          {STEP_LABELS.map((_, i) => (
            <div key={i} className={`h-1.5 rounded-full ${step >= i + 1 ? "bg-[var(--bq-orange)]" : "bg-[var(--bq-border)]"}`} data-testid={`step-bar-${i + 1}`} />
          ))}
        </div>
        <div className="grid grid-cols-6 gap-2 mb-6 text-xs uppercase tracking-widest text-[var(--bq-text-muted)] font-semibold">
          {STEP_LABELS.map((l, i) => (
            <div key={l} className={i + 1 === step ? "text-[var(--bq-navy)]" : ""}>{l}</div>
          ))}
        </div>

        <div className="bq-card p-6 md:p-8">
          {step === 1 && <Step1 form={form} set={set} />}
          {step === 2 && <Step2 categories={categories} loading={loadingCatalog} form={form} toggle={toggleCategory} />}
          {step === 3 && <Step3 services={allServices} categories={categories} form={form} toggle={toggleService} flags={flags} />}
          {step === 4 && <Step4 form={form} setForm={setForm} flags={flags} />}
          {step === 5 && <Step5 files={files} onFiles={onFiles} uploading={uploading} removeFile={removeFile} />}
          {step === 6 && <Step6 form={form} setForm={setForm} flags={flags} />}

          {error && (
            <div className="mt-5 text-sm text-[var(--bq-red)] bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="submit-error">
              {error}
            </div>
          )}

          <div className="mt-8 flex justify-between">
            <button onClick={back} disabled={step === 1}
              data-testid="step-back"
              className="inline-flex items-center gap-1 px-4 py-2.5 rounded-md text-sm font-semibold border border-[var(--bq-border)] hover:border-[var(--bq-navy)] disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft size={14} /> Back
            </button>
            {step < STEP_LABELS.length ? (
              <button onClick={next} disabled={!canAdvance()}
                data-testid="step-next"
                className="inline-flex items-center gap-1 bq-navy-btn px-5 py-2.5 rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                Continue <ChevronRight size={14} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting}
                data-testid="submit-project-btn"
                className="bq-orange-btn px-5 py-2.5 rounded-md font-semibold flex items-center gap-2 disabled:opacity-60">
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    AI is reading your services…
                  </>
                ) : (
                  <>Submit &amp; get AI estimate</>
                )}
              </button>
            )}
          </div>
          {submitting && (
            <p className="mt-3 text-xs text-[var(--bq-text-muted)] text-right" data-testid="ai-thinking-hint">
              This usually takes 5–15 seconds. We're sending your selections to Claude for a costed range.
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}

const inputCls = "w-full px-3 py-2.5 bg-white border border-[var(--bq-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--bq-orange)]";
function chipCls(active) {
  return `px-4 py-2.5 rounded-md text-sm font-medium border cursor-pointer text-center transition-all ${
    active ? "border-[var(--bq-orange)] bg-[var(--bq-orange)] text-white" : "border-[var(--bq-border)] bg-white hover:border-[var(--bq-navy)]"
  }`;
}
function Field({ label, children, hint }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-xs text-[var(--bq-text-muted)] mt-1">{hint}</div>}
    </div>
  );
}

// =============== STEPS ===============
function Step1({ form, set }) {
  return (
    <div className="space-y-5" data-testid="step-1">
      <Field label="Project title">
        <input value={form.title} onChange={set("title")} required
          data-testid="field-title"
          placeholder="e.g. Kitchen + scullery renovation"
          className={inputCls} />
      </Field>
      <Field label="Project type">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PROJECT_TYPES.map((p) => (
            <button key={p.v} type="button" onClick={() => set("project_type")(p.v)}
              data-testid={`ptype-${p.v}`}
              className={`text-left p-4 rounded-md border transition-all ${
                form.project_type === p.v ? "border-[var(--bq-orange)] bg-orange-50/40" : "border-[var(--bq-border)] bg-white hover:border-[var(--bq-navy)]"
              }`}>
              <div className="font-semibold">{p.l}</div>
              <div className="text-xs text-[var(--bq-text-muted)] mt-1">{p.d}</div>
            </button>
          ))}
        </div>
      </Field>
    </div>
  );
}

function Step2({ categories, loading, form, toggle }) {
  return (
    <div className="space-y-5" data-testid="step-2">
      <div>
        <div className="overline">Which areas?</div>
        <h3 className="font-display text-xl font-bold mt-1">Pick the parts of the property you want to work on</h3>
        <p className="text-sm text-[var(--bq-text-muted)] mt-1">Select all that apply — we'll show the matching services next.</p>
      </div>
      {loading ? (
        <div className="overline py-6">Loading categories…</div>
      ) : categories.length === 0 ? (
        <div className="text-sm text-[var(--bq-text-muted)]">No categories available for this project type.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="categories-grid">
          {categories.map((c) => {
            const active = form.selectedCategoryIds.includes(c.id);
            return (
              <button key={c.id} type="button" onClick={() => toggle(c.id)}
                data-testid={`cat-${c.slug}`}
                className={`text-left p-4 rounded-md border transition-all relative ${
                  active ? "border-[var(--bq-orange)] bg-orange-50/40" : "border-[var(--bq-border)] bg-white hover:border-[var(--bq-navy)]"
                }`}>
                <div className="font-semibold">{c.name}</div>
                {c.description && <div className="text-xs text-[var(--bq-text-muted)] mt-1 line-clamp-2">{c.description}</div>}
                {active && (
                  <Check size={16} className="absolute top-2 right-2 text-[var(--bq-orange)]" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Step3({ services, categories, form, toggle, flags }) {
  const [search, setSearch] = useState("");
  const grouped = useMemo(() => {
    const byCat = {};
    for (const s of services) {
      if (search && !s.service_name.toLowerCase().includes(search.toLowerCase())) continue;
      (byCat[s.category_id] ||= []).push(s);
    }
    return byCat;
  }, [services, search]);
  const catName = (id) => categories.find((c) => c.id === id)?.name || "Services";

  return (
    <div className="space-y-5" data-testid="step-3">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">Choose services</div>
          <h3 className="font-display text-xl font-bold mt-1">What needs doing?</h3>
        </div>
        <div className="relative w-full md:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--bq-text-muted)]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter services…"
            data-testid="service-search"
            className="w-full pl-9 pr-3 py-2 bg-white border border-[var(--bq-border)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--bq-orange)]" />
        </div>
      </div>

      {/* Flag chips */}
      {form.selectedServiceIds.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {flags.surveyRequired && <span className="pill pill-quote">Site survey recommended</span>}
          {flags.engineerRequired && <span className="pill pill-progress">Engineer signoff needed</span>}
          {flags.municipalApproval && <span className="pill pill-submitted">Municipal approval likely</span>}
          {flags.avgDuration > 0 && <span className="pill pill-default">~{flags.avgDuration} days avg/item</span>}
        </div>
      )}

      {services.length === 0 ? (
        <div className="text-sm text-[var(--bq-text-muted)]">Pick at least one area in the previous step to see services.</div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([cid, list]) => (
            <div key={cid}>
              <div className="overline mb-2">{catName(cid)}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {list.map((s) => {
                  const active = form.selectedServiceIds.includes(s.id);
                  return (
                    <button key={s.id} type="button" onClick={() => toggle(s.id)}
                      data-testid={`svc-${s.service_code}`}
                      className={`text-left p-3 rounded-md border transition-all flex items-start gap-3 ${
                        active ? "border-[var(--bq-orange)] bg-orange-50/40" : "border-[var(--bq-border)] bg-white hover:border-[var(--bq-navy)]"
                      }`}>
                      <span className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center ${active ? "bg-[var(--bq-orange)] border-[var(--bq-orange)]" : "border-[var(--bq-border)]"}`}>
                        {active && <Check size={12} className="text-white" />}
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm font-medium">{s.service_name}</span>
                        <span className="block text-xs text-[var(--bq-text-muted)] mt-0.5">
                          {s.complexity_level} · per {s.unit_type.toLowerCase()}
                          {s.requires_engineer && " · engineer"}
                          {s.requires_municipal_approval && " · municipal"}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Step4({ form, setForm, flags }) {
  return (
    <div className="space-y-5" data-testid="step-4">
      <Field label="Site address">
        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required
          data-testid="field-address"
          placeholder="123 Long Street, Cape Town"
          className={inputCls} />
      </Field>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Approximate area size (m²)" hint="Total area affected, if known">
          <input type="number" min="0" value={form.scope_details.area_size_m2}
            onChange={(e) => setForm({ ...form, scope_details: { ...form.scope_details, area_size_m2: e.target.value } })}
            data-testid="field-area"
            className={inputCls} placeholder="e.g. 45" />
        </Field>
        <Field label="Rooms / units affected (optional)">
          <input type="number" min="0" value={form.scope_details.num_rooms}
            onChange={(e) => setForm({ ...form, scope_details: { ...form.scope_details, num_rooms: e.target.value } })}
            data-testid="field-rooms"
            className={inputCls} placeholder="e.g. 2" />
        </Field>
      </div>

      <Field label="Anything else our estimator should know?" hint="Current condition, materials, brand preferences, etc.">
        <textarea rows={3} value={form.scope_details.notes}
          onChange={(e) => setForm({ ...form, scope_details: { ...form.scope_details, notes: e.target.value } })}
          data-testid="field-scope-notes"
          className={inputCls} />
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Latitude (optional)">
          <input type="number" step="any" value={form.gps_lat ?? ""}
            onChange={(e) => setForm({ ...form, gps_lat: e.target.value === "" ? null : parseFloat(e.target.value) })}
            data-testid="field-lat" className={inputCls} placeholder="-33.9249" />
        </Field>
        <Field label="Longitude (optional)">
          <input type="number" step="any" value={form.gps_lng ?? ""}
            onChange={(e) => setForm({ ...form, gps_lng: e.target.value === "" ? null : parseFloat(e.target.value) })}
            data-testid="field-lng" className={inputCls} placeholder="18.4241" />
        </Field>
      </div>

      {(flags.engineerRequired || flags.municipalApproval) && (
        <div className="border border-amber-200 bg-amber-50 rounded-md p-3 flex items-start gap-2 text-sm" data-testid="risk-banner">
          <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-800">Heads up</div>
            <div className="text-amber-800/90 text-xs mt-1">
              Based on the services you picked, this project will likely need
              {flags.engineerRequired ? " engineer signoff" : ""}
              {flags.engineerRequired && flags.municipalApproval ? " and" : ""}
              {flags.municipalApproval ? " municipal approval" : ""}.
              We'll flag this for our estimator.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Step5({ files, onFiles, uploading, removeFile }) {
  return (
    <div className="space-y-5" data-testid="step-5">
      <Field label="Plans, drawings, photos, videos (optional)" hint="PDF, images and short video clips help our estimator a lot">
        <label className="block border-2 border-dashed border-[var(--bq-border)] rounded-md p-8 text-center cursor-pointer hover:border-[var(--bq-orange)] transition-all" data-testid="file-dropzone">
          <input type="file" multiple onChange={onFiles} className="hidden"
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,video/mp4"
            data-testid="file-input" />
          <Upload size={20} className="mx-auto text-[var(--bq-text-muted)]" />
          <div className="mt-2 text-sm font-medium">Click to upload</div>
          <div className="overline mt-1">JPG, PNG, WEBP, PDF, MP4 — up to 25MB</div>
        </label>
        {uploading && (
          <div className="mt-3 flex items-center gap-2 text-sm text-[var(--bq-text-muted)]" data-testid="upload-progress">
            <Loader2 className="animate-spin" size={14} /> Uploading…
          </div>
        )}
        {files.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid="file-list">
            {files.map((f) => (
              <li key={f.id} className="flex justify-between items-center text-sm bg-[var(--bq-bg)] px-3 py-2 rounded-md" data-testid={`file-${f.id}`}>
                <span className="truncate">{f.original_filename} <span className="text-[var(--bq-text-muted)] text-xs">({Math.round(f.size/1024)} KB)</span></span>
                <button onClick={() => removeFile(f.id)} className="text-[var(--bq-text-muted)] hover:text-[var(--bq-red)]" data-testid={`remove-file-${f.id}`}>
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Field>
    </div>
  );
}

function Step6({ form, setForm, flags }) {
  return (
    <div className="space-y-5" data-testid="step-6">
      <Field label="Budget range">
        <div className="grid sm:grid-cols-2 gap-3">
          {BUDGETS.map((b) => (
            <label key={b.v} className={chipCls(form.budget_range === b.v)} data-testid={`budget-${b.v}`}>
              <input type="radio" name="budget" value={b.v} checked={form.budget_range === b.v}
                onChange={(e) => setForm({ ...form, budget_range: e.target.value })} className="hidden" />
              {b.l}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Timeline">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TIMELINES.map((t) => (
            <label key={t.v} className={chipCls(form.timeline === t.v)} data-testid={`timeline-${t.v}`}>
              <input type="radio" name="timeline" value={t.v} checked={form.timeline === t.v}
                onChange={(e) => setForm({ ...form, timeline: e.target.value })} className="hidden" />
              {t.l}
            </label>
          ))}
        </div>
      </Field>

      <div className="bg-[var(--bq-bg)] border border-dashed border-[var(--bq-border)] rounded-md p-4 text-sm flex gap-2">
        <Sparkles size={16} className="text-[var(--bq-orange)] shrink-0 mt-0.5" />
        <span>
          On submission, our AI will read your <b>{form.selectedServiceIds.length}</b> selected service(s)
          {flags.surveyRequired ? ", flag a site survey," : ","} and return a confidence-scored cost range.
        </span>
      </div>
    </div>
  );
}
